// faceTecService.js - Updated to include verification functionality
import { Config } from "../../Config.js";
import { LivenessCheckProcessor } from "../processors/LivenessCheckProcessor.js";
import { VerificationProcessor } from "../processors/VerificationProcessor.js";

// Following the documentation pattern for MyFaceTecApp class exactly
class MyFaceTecApp {
  constructor() {
    this.isInitialized = false;
  }

  // Following their init() function pattern exactly from the documentation
  init = () => {
    // Set the directory path for FaceTec Browser SDK Resources.
    window.FaceTecSDK.setResourceDirectory("/core-sdk/FaceTecSDK.js/resources");

    // Set the directory path for FaceTec Browser SDK images.
    window.FaceTecSDK.setImagesDirectory("/core-sdk/FaceTec_images");

    // Use the Config.js initialization function instead of direct call
    Config.initializeFromAutogeneratedConfig(window.FaceTecSDK, (initializationSuccessful) => {
      console.log("Initialization Result: ", initializationSuccessful);
      this.isInitialized = initializationSuccessful;
    });
  };

  // Following their onLivenessCheckPressed() function pattern exactly
  onLivenessCheckPressed = (callback) => {
    // Get a Session Token from the FaceTec SDK, then start the 3D Liveness Check.
    this.getSessionToken((sessionToken) => {
      const livenessCheckProcessor = new LivenessCheckProcessor(sessionToken, (result) => {
        console.log("Liveness Check Complete", result);
        if (callback) {
          callback(result);
        }
      });
    });
  };

  // NEW: Verification function for 1:1 matching
  onVerificationPressed = (userId, callback) => {
    // Get a Session Token from the FaceTec SDK, then start the 3D Verification.
    this.getSessionToken((sessionToken) => {
      if (sessionToken) {
        const verificationProcessor = new VerificationProcessor(sessionToken, userId, (result) => {
          console.log("Verification Complete", result);
          if (callback) {
            callback(result);
          }
        });
      } else {
        console.error('Failed to get session token for verification');
        if (callback) {
          callback({
            success: false,
            error: 'Failed to get session token'
          });
        }
      }
    });
  };

  // Following their getSessionToken function exactly from the documentation
  getSessionToken = (sessionTokenCallback) => {
    const XHR = new XMLHttpRequest();
    XHR.open("POST", Config.SessionTokenURL);
    XHR.setRequestHeader("Content-Type", "application/json");

    const requestBody = JSON.stringify({
      user_agent: window.FaceTecSDK.createFaceTecAPIUserAgentString("")
    });
    
    XHR.onreadystatechange = function() {
      if (this.readyState === XMLHttpRequest.DONE) {
        try {
          const response = JSON.parse(this.responseText);
          const sessionToken = response.sessionToken;
          sessionTokenCallback(sessionToken);
        } catch (error) {
          console.error("Error getting session token:", error);
          sessionTokenCallback(null);
        }
      }
    };
    
    XHR.onerror = function() {
      console.error("Network error getting session token");
      sessionTokenCallback(null);
    };
    
    XHR.send(requestBody);
  };

  // Check if SDK is initialized
  isSDKInitialized = () => {
    return this.isInitialized && window.FaceTecSDK && window.FaceTecSDK.getStatus() === window.FaceTecSDK.FaceTecSDKStatus.Initialized;
  };

  // Apply customization (following their pattern)
  applyCustomization = () => {
    if (!this.isSDKInitialized()) {
      console.error("SDK not initialized. Call init() first.");
      return;
    }
    
    // Apply the configuration wizard customization
    window.FaceTecSDK.setCustomization(Config.retrieveConfigurationWizardCustomization(window.FaceTecSDK));
  };
}

// Create singleton instance
export const faceTecApp = new MyFaceTecApp();

// Export convenience functions for React components
export const initializeFaceTecSDK = async () => {
  return new Promise((resolve, reject) => {
    try {
      faceTecApp.init();
      
      // Wait for initialization to complete
      const checkInitialization = (attempts = 0) => {
        if (faceTecApp.isSDKInitialized()) {
          faceTecApp.applyCustomization();
          resolve(true);
        } else if (attempts < 50) { // 5 second timeout
          setTimeout(() => checkInitialization(attempts + 1), 100);
        } else {
          reject(new Error('FaceTec SDK initialization timeout'));
        }
      };
      
      checkInitialization();
    } catch (error) {
      reject(error);
    }
  });
};

export const startLivenessCheck = (callback) => {
  faceTecApp.onLivenessCheckPressed(callback);
};

export const startVerification = (userId, callback) => {
  faceTecApp.onVerificationPressed(userId, callback);
};